import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { FileText, UserPlus, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const { signup, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Email and password are required");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await signup(email.trim(), password, name.trim());
      toast.success("Account created — welcome");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 p-6">
      <Card className="w-full max-w-md p-8" data-testid="signup-card">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-md bg-emerald-600 text-white flex items-center justify-center">
            <FileText size={18} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500">Get started</div>
            <h1 className="text-xl font-bold tracking-tight">Create your account</h1>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Name (optional)
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-10 mt-1 text-sm"
              placeholder="Your name or company"
              data-testid="signup-name"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Email</Label>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-10 mt-1 text-sm"
              placeholder="you@company.in"
              data-testid="signup-email"
              required
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Password (min 6 chars)
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-10 mt-1 text-sm"
              data-testid="signup-password"
              required
              minLength={6}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">
              Confirm password
            </Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-10 mt-1 text-sm"
              data-testid="signup-confirm"
              required
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700 h-10"
            disabled={loading}
            data-testid="signup-submit"
          >
            {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : <UserPlus size={16} className="mr-2" />}
            {loading ? "Creating…" : "Create Account"}
          </Button>
        </form>

        <div className="text-xs text-zinc-500 text-center mt-6">
          Already have an account?{" "}
          <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="link-login">
            Sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
